<script setup lang="ts">
import {
    faPlaneArrival,
    faPlaneDeparture,
} from '@fortawesome/pro-regular-svg-icons';
import FlightInfoCard from './_components/FlightInfoCard.vue';
import LandingTabs from '@components/ui/index&flight-info/LandingTabs.vue';

const { search = '', filter } = defineProps<{
    search?: string;
    filter?: 'all' | 'domestic' | 'international';
}>();
const contentStore = useContentStore();
const { landingPageTitle } = classToRefs(contentStore);
const flightInfoStore = useFlightInfoStore();
const { inboundFlights, outboundFlights } = classToRefs(flightInfoStore);
const route = useRoute();
await useAsyncData(async () => {
    await flightInfoStore.fetchAirports();
    const id = route.params.airport;
    await flightInfoStore.fetchFlights(id as string);
    return true;
});
useSeoMeta({ title: landingPageTitle });
const direction = ref<'in' | 'out'>('out');
const tabItems = [
    {
        id: 'out',
        title: 'پروازهای خروجی',
        icon: faPlaneDeparture,
    },
    {
        id: 'in',
        title: 'پروازهای ورودی',
        icon: faPlaneArrival,
    },
];
const isLoading = ref(false);
const interval = ref<number>();
const source = computed(() =>
    direction.value === 'in' ? inboundFlights.value : outboundFlights.value
);
const errorText = computed(() => {
    if (!source.value.length) {
        return `در حال حاضر پرواز ${
            direction.value === 'in' ? 'ورودی' : 'خروجی'
        } در فرودگاه انتخابی ثبت نشده است.`;
    }
    if (search && !filteredResults.value.length) {
        return 'هیچ پروازی با فیلتر مورد نظر شما یافت نشد.';
    }
    return '';
});
const filteredResults = computed(() => {
    const dynamicField = direction.value === 'in' ? 'origin' : 'destination';
    return (
        !search
            ? source.value
            : source.value.filter(
                  flight =>
                      flight.flightNumber?.toString().includes(search) ||
                      flight[dynamicField]?.includes(search) ||
                      flight.airline?.includes(search)
              )
    ).filter(({ isDomestic }) =>
        filter === 'all' || filter === 'domestic' ? isDomestic : !isDomestic
    );
});

useSchemaOrg([
    defineListItem({
        '@context': 'https://schema.org',
        '@type': 'ListItem',
        itemListElement: (filteredResults.value ?? []).map((item, index) => ({
            '@type': 'Flight',
            position: index + 1,
            departureAirport: item.airline,
            arrivalTime: item.scheduledTime,
            seller: 'mrbilit',
            tripOrigin: item.origin,
            flightNumber: item.flightNumber,
        })),
    }),
]);

onMounted(() => {
    interval.value = window.setInterval(() => {
        const id = route.params.airport;
        flightInfoStore.fetchFlights(id as string);
    }, 60000);
});
onBeforeUnmount(() => {
    if (interval.value) {
        window.clearInterval(interval.value);
    }
});
</script>

<template>
    <table class="cards-container">
        <thead>
            <tr>
                <th>
                    <LandingTabs
                        v-model="direction"
                        :items="tabItems"
                        class="tabs"
                    />
                </th>
            </tr>
        </thead>
        <mr-loading v-if="isLoading" />
        <mr-typography v-else-if="errorText" weight="700">
            {{ errorText }}
        </mr-typography>
        <tbody v-else-if="filteredResults.length" class="cards-table">
            <template
                v-for="(item, index) in filteredResults"
                :key="item.flightId"
            >
                <tr v-if="!(index % 2)">
                    <FlightInfoCard :item :direction class="card" />
                    <FlightInfoCard
                        v-if="filteredResults[index + 1]"
                        :item="filteredResults[index + 1]"
                        :direction
                        class="card"
                    />
                </tr>
            </template>
        </tbody>
        <mr-typography v-else weight="500">
            پروازی برای نمایش موجود نیست.
        </mr-typography>
    </table>
</template>
<style lang="scss" scoped>
.cards-container {
    width: 100%;
    max-width: $app-container-max-width;
    background-color: white;
    border-radius: 12px;
    border-collapse: separate;
    padding: 32px;
    margin-top: 20px;
    @include shadow();
    @include mobile-only {
        padding: 16px;
        margin-top: 0;
        border-radius: 0;
    }
    .tabs {
        margin-bottom: 32px;
    }

    .cards-table {
        width: 100%;
        tr {
            display: flex;
            margin-bottom: 16px;
            flex-wrap: wrap;
            .card {
                flex-basis: calc(50% - 8px);
                max-width: calc(50% - 8px);

                &:last-child {
                    margin-bottom: 0;
                }

                &:nth-last-child(2):nth-child(odd) {
                    margin-bottom: 0;
                    @include mobile-only(900px) {
                        margin-bottom: 16px;
                    }
                }

                &:nth-child(even) {
                    margin-right: 8px;
                    @include mobile-only(900px) {
                        margin-right: 0;
                    }
                }

                &:nth-child(odd) {
                    margin-left: 8px;
                    @include mobile-only(900px) {
                        margin-left: 0;
                    }
                }

                @include mobile-only(900px) {
                    flex-basis: 100%;
                    max-width: 100%;
                    margin-bottom: 16px;
                }
            }
        }
    }
}
</style>
